/**
 * AuthorListView — Primary stage view: grid of philosopher cards.
 *
 * Reacts to filter changes emitted by the sidebar FilterPanel via the bus.
 * On card click, emits `navigate:author` which the StageRouter handles.
 */

import { BaseComponent } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type { PhilosopherFilters } from '@/services/IDataService';
import type { PhilosopherLocalized } from '@/data/schema';
import { formatLifespan } from './format';

export class AuthorListView extends BaseComponent {
  private filters: PhilosopherFilters = {};
  private gridEl!: HTMLElement;
  /** click delegato su gridEl: attaccare UNA volta (render() gira ad ogni refresh). */
  private wired = false;
  private countEl!: HTMLElement;
  private allAuthors: PhilosopherLocalized[] = [];

  protected async onInit(): Promise<void> {
    this.element.classList.add('author-list-view');
    this.element.innerHTML = `
      <header class="view-header">
        <h1 class="view-title">Filosofi</h1>
        <p class="view-subtitle">
          <span data-role="count"></span> pensatori nel catalogo
        </p>
      </header>
      <div class="author-grid" data-role="grid"></div>
    `;
    this.gridEl = this.element.querySelector<HTMLElement>('[data-role="grid"]')!;
    this.countEl = this.element.querySelector<HTMLElement>('[data-role="count"]')!;

    // Listen to filter changes from the sidebar
    this.addCleanup(
      this.context.bus.on<PhilosopherFilters>('filter:change', (filters) => {
        this.filters = filters;
        this.refresh().catch(console.error);
      }),
    );

    await this.refresh();
  }

  private async refresh(): Promise<void> {
    const data = getDataService();
    this.allAuthors = await data.listAuthors(this.filters, this.context.locale);
    this.render();
  }

  private render(): void {
    this.countEl.textContent = String(this.allAuthors.length);

    if (this.allAuthors.length === 0) {
      this.gridEl.innerHTML = `
        <div class="empty-state">
          Nessun filosofo corrisponde ai filtri selezionati.
        </div>
      `;
      return;
    }

    this.gridEl.innerHTML = this.allAuthors
      .map((a) => this.renderCard(a))
      .join('');

    // Event delegation for card clicks — attaccato UNA volta (render() gira ad
    // ogni filtro/locale; gridEl persiste, il delegato sopravvive all'innerHTML).
    if (this.wired) return;
    this.wired = true;
    this.addEventListener(this.gridEl, 'click', (ev) => {
      const target = ev.target as HTMLElement;
      const card = target.closest<HTMLElement>('[data-author-id]');
      if (!card) return;
      const id = card.dataset.authorId;
      if (id) {
        this.context.bus.emit('navigate:author', { id });
      }
    });
  }

  private renderCard(a: PhilosopherLocalized): string {
    // Local WebP portrait fetched at build time by scripts/fetch-author-portraits.ts.
    // Fallback: an inline placeholder is swapped in via the onerror handler if
    // the local file is missing (e.g., portrait wasn't fetched yet).
    const imgTag = `<img
      class="author-card-img"
      src="/images/authors/${escapeAttr(a.id)}.webp"
      alt="${escapeAttr(a.nome)}"
      loading="lazy"
      onerror="this.outerHTML='<div class=&quot;author-card-img author-card-img-placeholder&quot;>?</div>'"
    />`;

    const scuola = a.scuola?.[0] ?? '';

    return `
      <article class="author-card" data-author-id="${escapeAttr(a.id)}" tabindex="0">
        <div class="author-card-img-wrap">
          ${imgTag}
        </div>
        <div class="author-card-body">
          <h3 class="author-card-name">${escapeText(a.nome)}</h3>
          <p class="author-card-meta">${escapeText(formatLifespan(a.anno_nascita, a.anno_morte))}</p>
          ${scuola ? `<p class="author-card-school">${escapeText(scuola)}</p>` : ''}
        </div>
      </article>
    `;
  }

  protected onLocaleChange(): void {
    this.refresh().catch(console.error);
  }
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return escapeText(s).replace(/"/g, '&quot;');
}
